import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { ExactDeliverySlot } from '../client/services';
import { toastHandler } from '../utils/toastHandler';

interface ExactDeliverySlotSelectorProps {
  orderId: string;
  deliveryDate?: string;
  onSlotUpdate?: (slot: string) => void;
  allowEdit?: boolean;
  hideSaveButton?: boolean;
}

export interface ExactDeliverySlotSelectorRef {
  handleSave: () => Promise<void>;
  canSave: () => boolean;
  isLoading: () => boolean;
}

const ExactDeliverySlotSelector = forwardRef<ExactDeliverySlotSelectorRef, ExactDeliverySlotSelectorProps>(({
  orderId,
  deliveryDate,
  onSlotUpdate,
  allowEdit = true,
  hideSaveButton = false,
}, ref) => {
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [existingSlot, setExistingSlot] = useState<string | null>(null);
  const [isLate, setIsLate] = useState<boolean>(false);
  const [markedLateAt, setMarkedLateAt] = useState<string | null>(null);

  const timeSlots = [
    '11:00 AM - 12:00 PM',
    '12:00 PM - 1:00 PM',
    '1:00 PM - 2:00 PM',
    '2:00 PM - 3:00 PM',
    '3:00 PM - 4:00 PM',
    '4:00 PM - 5:00 PM',
    '5:00 PM - 6:00 PM',
    '6:00 PM - 7:00 PM',
    '7:00 PM - 8:00 PM',
    '8:00 PM - 9:00 PM',
    '9:00 PM - 10:00 PM',
    '10:00 PM - 11:00 PM',
  ];

  useEffect(() => {
    fetchExistingSlot();
  }, [orderId]);

  const fetchExistingSlot = async () => {
    try {
      setFetching(true);
      const result = await ExactDeliverySlot.find({
        query: { orderId },
      });
      
      if (result && result.exactDeliverySlot) {
        setExistingSlot(result.exactDeliverySlot);
        setSelectedSlot(result.exactDeliverySlot);
        setIsLate(result.isLate || false);
        setMarkedLateAt(result.markedLateAt || null);
      }
    } catch (error: any) {
      console.error('Error fetching exact delivery slot:', error);
    } finally {
      setFetching(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleSave,
    canSave: () => !loading && !!selectedSlot && selectedSlot !== existingSlot,
    isLoading: () => loading,
  }));

  const handleSave = async () => {
    if (!selectedSlot) {
      toastHandler({
        message: 'Please select an exact delivery slot',
        type: 'error',
      });
      return;
    }

    setLoading(true);

    try {
      // Convert UTC to Qatar time (UTC+3), then extract date part in YYYY-MM-DD format
      let dateToSend = deliveryDate || null;
      
      if (deliveryDate && deliveryDate.includes('T')) {
        // It's UTC format: "2025-12-10T23:07:16.742992Z"
        try {
          const utcDate = new Date(deliveryDate);
          if (!isNaN(utcDate.getTime())) {
            // Convert UTC to Qatar time (UTC+3)
            const qatarDate = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000)); // Add 3 hours
            
            // Extract date in YYYY-MM-DD format from Qatar time
            const year = qatarDate.getUTCFullYear();
            const month = String(qatarDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(qatarDate.getUTCDate()).padStart(2, '0');
            dateToSend = `${year}-${month}-${day}`; // "2025-12-11"
          }
        } catch (error) {
          console.error('Error converting delivery date to Qatar time:', error);
          // Fallback to original date if conversion fails
          dateToSend = deliveryDate;
        }
      } else if (deliveryDate && deliveryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Already in YYYY-MM-DD format, use as-is
        dateToSend = deliveryDate;
      }

      const response = await ExactDeliverySlot.create({
        orderId,
        exactDeliverySlot: selectedSlot,
        deliveryDate: dateToSend,
      });

      if (response && response.success) {
        setExistingSlot(selectedSlot);
        toastHandler({
          message: response.message || 'Exact delivery slot saved successfully',
          type: 'success',
        });
        
        if (onSlotUpdate) {
          onSlotUpdate(selectedSlot);
        }
      }
    } catch (error: any) {
      console.error('Error saving exact delivery slot:', error);
      toastHandler({
        message: error.message || 'Failed to save exact delivery slot',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Ready By?
      </Typography>
      <Box display="flex" gap={2} alignItems="center" mt={1}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 250, maxWidth: 300 }}>
          <Select
            value={selectedSlot}
            onChange={(e) => setSelectedSlot(e.target.value)}
            displayEmpty
            disabled={!allowEdit}
          >
            <MenuItem value="">
              <em>Select time slot</em>
            </MenuItem>
            {timeSlots.map((slot) => (
              <MenuItem key={slot} value={slot}>
                {slot}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!hideSaveButton && (
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={!allowEdit || loading || !selectedSlot || selectedSlot === existingSlot}
          >
            {loading ? <CircularProgress size={20} /> : 'Save Slot'}
          </Button>
        )}
      </Box>
      {existingSlot && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            Current slot: {existingSlot}
          </Typography>
          {isLate && (
            <Alert severity="error" sx={{ mt: 1 }} icon={<AccessTimeIcon />}>
              Order marked as LATE on {markedLateAt ? (() => {
                // Convert UTC to Doha time (UTC+3)
                const utcDate = new Date(markedLateAt);
                const dohaDate = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000)); // Add 3 hours
                
                // Format: DD/MM/YYYY, HH:mm:ss
                const day = String(dohaDate.getUTCDate()).padStart(2, '0');
                const month = String(dohaDate.getUTCMonth() + 1).padStart(2, '0');
                const year = dohaDate.getUTCFullYear();
                const hours = String(dohaDate.getUTCHours()).padStart(2, '0');
                const minutes = String(dohaDate.getUTCMinutes()).padStart(2, '0');
                const seconds = String(dohaDate.getUTCSeconds()).padStart(2, '0');
                
                return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
              })() : 'Unknown'}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
});

export default ExactDeliverySlotSelector;

